import React from 'react';
import { GlobeIcon, SmartphoneIcon, UserIcon, MailIcon, PhoneCallIcon, SparklesIcon, SearchIcon, DatabaseIcon, ShieldCheckIcon, SettingsIcon, UsersIcon, FileTextIcon, NetworkIcon, ArrowRightIcon, ArrowDownIcon } from 'lucide-react';
import { ATNA_LOGO, atnaUsers, atnaCore, atnaOps, type Bi, type EntryKey, type CoreKey, type OpsKey } from '../../data/atnaHero';
type P = {
  pick: (b: Bi) => string;
};
const ENTRY_ICONS: Record<EntryKey, typeof GlobeIcon> = {
  web: GlobeIcon,
  app: SmartphoneIcon,
  portal: UserIcon,
  email: MailIcon,
  phone: PhoneCallIcon,
  llm: SparklesIcon
};
const CORE_ICONS: Record<CoreKey, typeof GlobeIcon> = {
  understand: SearchIcon,
  context: DatabaseIcon,
  verify: ShieldCheckIcon,
  recommend: SparklesIcon,
  act: SettingsIcon
};
const OPS_ICONS: Record<OpsKey, typeof GlobeIcon> = {
  manual: UsersIcon,
  priorities: FileTextIcon,
  actions: SettingsIcon,
  services: NetworkIcon
};
const CARD = 'rounded-[22px] bg-white border border-[#EEEDF7] shadow-[0_24px_60px_-36px_rgba(49,46,129,0.35)]';
function CardHead({
  title,
  sub
}: {
  title: string;
  sub: string;
}) {
  return <div data-id="element-874" data-mp="3o74gtiivr2ho">
      <h3 className="font-sans text-[17px] font-semibold text-[#121A3A] tracking-tight" data-id="element-875" data-mp="3a9oa05wo96pe">{title}</h3>
      <p className="font-sans text-[13px] text-[#6B7390] mt-0.5" data-id="element-876" data-mp="3f8rg7toe1iyy">{sub}</p>
    </div>;
}
function UsersCard({
  pick,
  connectors
}: P & {
  connectors: boolean;
}) {
  const n = atnaUsers.items.length;
  return <div className={`${CARD} p-5`} data-id="element-877" data-mp="brspj0qfywbi">
      <CardHead title={pick(atnaUsers.title)} sub={pick(atnaUsers.sub)} data-id="element-878" data-mp="bqr3k17w0pf0" />
      <div className="relative mt-4" data-id="element-879" data-mp="3t923kmqhoffd">
        <ul className="flex flex-col gap-2" data-id="element-880" data-mp="3t80rqg1nfowl">
          {atnaUsers.items.map(it => {
          const Icon = ENTRY_ICONS[it.key];
          return <li key={it.key} className="relative flex items-center gap-3 rounded-xl bg-[#F7F7FC] px-3.5 py-2.5" data-id="element-881" data-mp="prbl6u22gf6x">
                <Icon className="w-[18px] h-[18px] text-[#4F46E5] shrink-0" strokeWidth={1.6} aria-hidden="true" data-id="element-882" data-mp="ujli4u1lmfip" />
                <span className="font-sans text-[14px] text-[#121A3A] whitespace-nowrap" data-id="element-883" data-mp="14e0j4rgo6kb7">{pick(it.label)}</span>
                {connectors && <span className="absolute -right-[18px] top-1/2 -translate-y-1/2 w-[7px] h-[7px] rounded-full bg-[#8B85F5]" aria-hidden="true" data-id="element-884" data-mp="ugcqq86jdow7" />}
              </li>;
        })}
        </ul>
        {connectors && <svg className="absolute left-[calc(100%+14px)] top-0 h-full w-[82px] overflow-visible" viewBox="0 0 74 600" preserveAspectRatio="none" aria-hidden="true" data-id="element-885" data-mp="lgg40c3pj03s">
            {atnaUsers.items.map((it, i) => {
          const y = (i + 0.5) / n * 600;
          return <path key={it.key} d={`M0 ${y} C 38 ${y}, 36 300, 74 300`} fill="none" stroke="#A7A2F4" strokeWidth={1.3} vectorEffect="non-scaling-stroke" data-id="element-886" data-mp="2p6va51rgfd9" data-mpi={i} />;
        })}
          </svg>}
      </div>
    </div>;
}
function AtnaCore({
  pick,
  compact
}: P & {
  compact?: boolean;
}) {
  return <div className={`relative mx-auto flex flex-col items-center justify-center text-center ${compact ? 'w-full max-w-[340px] rounded-[32px] py-9 px-6' : 'w-full aspect-[1/1.1] rounded-full px-6'} bg-[#FBFAFF] border border-white shadow-[0_0_0_10px_rgba(238,237,253,0.6),0_30px_80px_-30px_rgba(79,70,229,0.45),inset_0_0_60px_rgba(224,222,252,0.9)]`} data-id="element-887" data-mp="2idvimewlsi46">
      <img src={ATNA_LOGO} alt="ATNA" className="h-[54px] xl:h-[62px] w-auto object-contain" data-id="element-888" data-mp="1znvxu47spp6k" />
      <p className="mt-1 font-sans text-[11px] tracking-[0.3em] uppercase text-[#8C92AC] leading-[1.5]" data-id="element-889" data-mp="24e2osaps5lrz">
        {atnaCore.tagline.map(t => <span key={t.fr} className="block" data-id="element-890" data-mp="16yfc1peol6jb">{pick(t)}</span>)}
      </p>
      <ul className="mt-4 flex flex-col gap-2 text-left" data-id="element-891" data-mp="1bx02wy81mdv3">
        {atnaCore.items.map(it => {
        const Icon = CORE_ICONS[it.key];
        return <li key={it.key} className="flex items-center gap-3" data-id="element-892" data-mp="39lm3artdcocd">
              <Icon className="w-4 h-4 text-[#4F46E5] shrink-0" strokeWidth={1.7} aria-hidden="true" data-id="element-893" data-mp="39ngl3x9cg0c8" />
              <span className="font-sans text-[13.5px] text-[#121A3A] whitespace-nowrap" data-id="element-894" data-mp="3nebqv0ojq1bl">{pick(it.label)}</span>
            </li>;
      })}
      </ul>
    </div>;
}
function OpsCard({
  pick
}: P) {
  return <div className={`${CARD} p-5`} data-id="element-895" data-mp="17n7bq6ed2yv">
      <CardHead title={pick(atnaOps.title)} sub={pick(atnaOps.sub)} data-id="element-896" data-mp="3scwmsnwrlyu8" />
      <ul className="mt-4 flex flex-col gap-2.5" data-id="element-897" data-mp="a6ia0sa8fuxf">
        {atnaOps.items.map(it => {
        const Icon = OPS_ICONS[it.key];
        return <li key={it.key} className="flex items-center gap-4 rounded-xl bg-[#F7F7FC] px-4 min-h-[60px]" data-id="element-898" data-mp="66841fmfx4ik">
              <Icon className="w-6 h-6 text-[#4F46E5] shrink-0" strokeWidth={1.5} aria-hidden="true" data-id="element-899" data-mp="2bydhevpghd9u" />
              <span className="font-sans text-[14px] leading-snug text-[#121A3A]" data-id="element-900" data-mp="27y340xzc3kky">
                {it.label.map(l => <span key={l.fr} className="block" data-id="element-901" data-mp="1xuphej6ngi08">{pick(l)}</span>)}
              </span>
            </li>;
      })}
      </ul>
    </div>;
}
export function AtnaFlow({
  pick
}: P) {
  return <>
      {/* Desktop — left-to-right flow */}
      <div className="hidden lg:flex items-center justify-center max-w-[900px] mx-auto" data-id="element-902" data-mp="1szs95eh72coe">
        <div className="w-[28%] shrink-0" data-id="element-903" data-mp="1e2str4g3pfjd">
          <UsersCard pick={pick} connectors data-id="element-904" data-mp="1j3fd49y6fmqh" />
        </div>
        <div className="w-[88px] shrink-0 flex justify-end" aria-hidden="true" data-id="element-905" data-mp="1j50820oel7ov">
          <span className="w-2 h-2 rounded-full bg-[#6D66F0] shadow-[0_0_0_5px_rgba(79,70,229,0.12)] -mr-1" data-id="element-906" data-mp="154zaov7ckie5" />
        </div>
        <div className="flex-1 min-w-[220px] max-w-[280px]" data-id="element-907" data-mp="1a30rf9haw5mg">
          <AtnaCore pick={pick} data-id="element-908" data-mp="2zhfdtn4ujges" />
        </div>
        <div className="w-[56px] shrink-0 flex justify-center" aria-hidden="true" data-id="element-909" data-mp="2zfm3nxv0ye21">
          <ArrowRightIcon className="w-6 h-6 text-[#6D66F0]" strokeWidth={1.4} data-id="element-910" data-mp="34g0etorxa44a" />
        </div>
        <div className="w-[29%] shrink-0" data-id="element-911" data-mp="323oesyt1609k">
          <OpsCard pick={pick} data-id="element-912" data-mp="2y0rsoy4a7m0v" />
        </div>
      </div>

      {/* Tablet & mobile — vertical journey */}
      <div className="lg:hidden flex flex-col items-stretch gap-4 max-w-[520px] mx-auto" data-id="element-913" data-mp="2t16wgvghxrru">
        <UsersCard pick={pick} connectors={false} data-id="element-914" data-mp="2o2lryju7olxl" />
        <ArrowDownIcon className="w-6 h-6 text-[#6D66F0] mx-auto" strokeWidth={1.5} aria-hidden="true" data-id="element-915" data-mp="2o3wuyhfu1pnn" />
        <AtnaCore pick={pick} compact data-id="element-916" data-mp="3lpkp0votfvw0" />
        <ArrowDownIcon className="w-6 h-6 text-[#6D66F0] mx-auto" strokeWidth={1.5} aria-hidden="true" data-id="element-917" data-mp="3gr9iouxqc3ij" />
        <OpsCard pick={pick} data-id="element-918" data-mp="3c019o00bak5w" />
      </div>
    </>;
}